import React, { useEffect, useMemo, useRef, useState } from "react";
import { Panel, PanelGroup, PanelResizeHandle } from "react-resizable-panels";
import { useParams } from "react-router-dom";
import { buildFluidForm, mergeFormData } from "../../shared";
import { Icon } from "semantic-ui-react";
import _ from "lodash";
import { FluidFormConfigClient, useFluidFormConfigClient } from "./fluidFormConfig.client";
import styled from "styled-components";
import { FormBuilderLayout } from "./FormBuilderLayout";
import { BuilderPanelGroup } from "./BuilderPanelGroup";
import { FluidFormPreview } from "./PreviewFormPanel";
import { SaveFailedBanner } from "./SaveFailedBanner";
import { useAuth } from "@clerk/clerk-react";
import { getFormInstance } from "../fluidComponents/fluidForm/FormInstance.service";
import { store } from "../fluidComponents/fluidForm/Store";
import { registerWorkspaceInstances, WorkspaceInstances } from "./formInstances";
import { useUndoRedoHistory } from "./useUndoRedoHistory";
import TemplateToggleButton from "./TemplateToggleButton";
import { PageLoader } from "../components/PageLoader";
import { BuilderTour } from "./BuilderTour";
const WorkspaceContainer = styled.div.attrs({
    className: 'builder-workspace-container'
}) `
  /* Fill the available space within the ContentContainer */
  height: calc(100vh); /* Navigation height (45px) + ContentContainer padding (4rem) */
  width: 100%;
  position: relative;
`;
const ResizeHandle = styled(PanelResizeHandle).attrs({
    className: 'builder-resize-handle'
}) `
  background: #e1e5e9;
  transition: background 0.2s ease;
  position: relative;
  
  &:hover {
    background: #4a90e2;
  }
  
  &[data-resize-handle-active] {
    background: #357abd;
  }
  
  &[data-panel-group-direction="horizontal"] {
    width: 6px;
    cursor: col-resize;
    
    &::after {
      content: '';
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      width: 2px;
      height: 20px;
      background: rgba(255, 255, 255, 0.5);
      border-radius: 1px;
    }
  }
  
  &:hover::after {
    background: rgba(255, 255, 255, 0.8);
  }
`;
export const BuilderWorkspacePage = () => {
    const { formKey } = useParams();
    if (!formKey) {
        return React.createElement("div", null, "Form key is required");
    }
    return React.createElement(FormBuilderLayout, { formKey: formKey },
        React.createElement(BuilderWorkspace, { formKey: formKey }));
};
function applyPlanRestrictions(plan, form) {
    if (plan === 'free_org') {
        form.setValue('showBranding', true);
        form.getConfig('showBranding').readOnly = true;
    }
    return form;
}
function usePlan() {
    const { has } = useAuth();
    if (has({ plan: 'pro_plan' })) {
        return 'pro_plan';
    }
    return 'free_org';
}
async function loadFormConfigForPreview(formKey) {
    let configClient = new FluidFormConfigClient();
    // @TODO: FLU-193 Handle this.
    let formConfigRecord = await configClient.getFormConfig(formKey);
    for (let formConfigKey in WorkspaceInstances) {
        if (formConfigKey === 'previewForm') {
            continue;
        }
        let additionalConfigForm = getFormInstance(WorkspaceInstances[formConfigKey]).fluidForm;
        additionalConfigForm.mergeSubmissionData(formConfigRecord[formConfigKey]);
        //Sets the submission data in the current config record to ensure consistency
        formConfigRecord[formConfigKey] = additionalConfigForm.getFormSubmission();
    }
    store.dispatch(WorkspaceInstances.previewForm, 'setForm', buildFluidForm(formConfigRecord));
    return formConfigRecord;
}
export function BuilderWorkspace({ formKey }) {
    return React.createElement(BuilderWorkspaceComponent, { key: formKey, formKey: formKey });
}
export function BuilderWorkspaceComponent({ formKey }) {
    const configClient = useFluidFormConfigClient();
    const [loading, setLoading] = useState(true);
    const [configRecord, setConfigRecord] = useState();
    const [isPreviewMaximized, setIsPreviewMaximized] = useState(false);
    const [panelGroupKey, setPanelGroupKey] = useState(0);
    const [saveStatus, setSaveStatus] = useState('idle');
    const [saveBannerDismissed, setSaveBannerDismissed] = useState(false);
    const prevSaveStatusRef = useRef('idle');
    const configRecordRef = useRef(undefined);
    const isApplyingHistoryRef = useRef(false);
    const history = useUndoRedoHistory({
        capacity: 100,
        coalesceMs: 250,
    });
    const toSaveComparable = (record) => ({
        formKey: record.formKey,
        name: record.name,
        options: record.options,
        code: record.code,
        formConfig: record.formConfig,
        styleConfig: record.styleConfig,
        actionsConfig: record.actionsConfig,
    });
    const lastSavedComparableRef = useRef(null);
    const pendingSaveRef = useRef(null);
    const pendingSaveComparableRef = useRef(null);
    const debouncedAutosave = useMemo(() => {
        return _.debounce(async () => {
            const payload = pendingSaveRef.current;
            const comparable = pendingSaveComparableRef.current;
            if (!payload || !comparable)
                return;
            if (lastSavedComparableRef.current && _.isEqual(lastSavedComparableRef.current, comparable)) {
                setSaveStatus('saved');
                return;
            }
            try {
                await configClient.saveFormConfig(payload);
                lastSavedComparableRef.current = _.cloneDeep(comparable);
                setSaveStatus('saved');
            }
            catch (error) {
                console.error("Error saving form config", error);
                setSaveStatus('error');
            }
        }, 2000, { leading: true, trailing: true });
    }, [configClient]);
    useEffect(() => {
        return () => {
            debouncedAutosave.cancel();
        };
    }, [debouncedAutosave]);
    useEffect(() => {
        if (saveStatus === 'error' && prevSaveStatusRef.current !== 'error') {
            setSaveBannerDismissed(false);
        }
        prevSaveStatusRef.current = saveStatus;
    }, [saveStatus]);
    const scheduleAutosave = (record) => {
        // Preserve existing save behavior (send full record), but de-dupe based on comparable config fields.
        const payload = {
            ...record,
            formKey,
        };
        pendingSaveRef.current = payload;
        pendingSaveComparableRef.current = toSaveComparable(payload);
        setSaveStatus('saving');
        debouncedAutosave();
    };
    useEffect(() => {
        const cleanup = registerWorkspaceInstances();
        return cleanup;
    }, []);
    useEffect(() => {
        configRecordRef.current = configRecord;
    }, [configRecord]);
    useEffect(() => {
        if (formKey) {
            setLoading(true);
            loadFormConfigForPreview(formKey).then((formConfigRecord) => {
                configRecordRef.current = formConfigRecord;
                setConfigRecord(formConfigRecord);
                history.reset(formConfigRecord);
                lastSavedComparableRef.current = _.cloneDeep(toSaveComparable(formConfigRecord));
                setSaveStatus('saved');
                setLoading(false);
            }).catch((error) => {
                // @TODO: FLU-193 Handle this.
                console.error("Error loading form config", error);
                setLoading(false);
            });
        }
    }, [formKey]);
    const switchTabRef = useRef(null);
    const isTemplate = !!configRecord?.isTemplate;
    const publishStatus = useMemo(() => {
        if (!configRecord)
            return 'not-published';
        const { currentVersionId, versions } = configRecord;
        if (!currentVersionId || !versions?.length)
            return 'not-published';
        const activeVersion = versions.find(v => v.versionId === currentVersionId);
        if (!activeVersion)
            return 'not-published';
        const draftMatch = _.isEqual(configRecord.formConfig, activeVersion.formConfig) &&
            _.isEqual(configRecord.actionsConfig ?? {}, activeVersion.actionsConfig ?? {}) &&
            _.isEqual(configRecord.styleConfig ?? {}, activeVersion.styleConfig ?? {});
        return draftMatch ? 'published' : 'unpublished-changes';
    }, [configRecord]);
    function updatePreviewForm(formConfig) {
        if (formConfig && formConfig?.fields) {
            store.dispatch(WorkspaceInstances.previewForm, 'setFormConfig', formConfig);
        }
    }
    function updatePreviewFormAdditionalConfigurations(configRecord) {
        for (let formConfigKey in WorkspaceInstances) {
            if (formConfigKey != WorkspaceInstances.previewForm) {
                store.dispatch(WorkspaceInstances.previewForm, 'mergeAdditionalConfigValue', formConfigKey, configRecord[formConfigKey]);
            }
        }
    }
    const handleConfigRecordChange = (updatedConfigRecord) => {
        const base = configRecordRef.current;
        if (!base)
            return;
        const mergedConfigRecord = mergeFormData(base, updatedConfigRecord);
        // No-op: don't update state, don't autosave, don't create history.
        if (_.isEqual(base, mergedConfigRecord)) {
            return;
        }
        configRecordRef.current = mergedConfigRecord;
        setConfigRecord(mergedConfigRecord);
        if (!isApplyingHistoryRef.current) {
            history.push(mergedConfigRecord);
        }
        applyChangesToBuilder(mergedConfigRecord);
    };
    const applySnapshot = (record) => {
        isApplyingHistoryRef.current = true;
        configRecordRef.current = record;
        setConfigRecord(record);
        applyChangesToBuilder(record);
        // Allow FormInstance onFormUpdated events to flush without creating history.
        setTimeout(() => {
            isApplyingHistoryRef.current = false;
        }, 0);
    };
    const applyChangesToBuilder = (record) => {
        // Sync all workspace forms to the snapshot so panels update.
        store.dispatch(WorkspaceInstances.formConfig, "setSubmissionData", record.formConfig);
        store.dispatch(WorkspaceInstances.styleConfig, "setSubmissionData", (record.styleConfig || {}));
        store.dispatch(WorkspaceInstances.actionsConfig, "setSubmissionData", (record.actionsConfig || {}));
        // Sync preview.
        updatePreviewForm(record.formConfig);
        updatePreviewFormAdditionalConfigurations(record);
        // Autosave for any config change (de-duped).
        scheduleAutosave(record);
    };
    const [showTour, setShowTour] = useState(false);
    if (loading) {
        return React.createElement(PageLoader, { text: "Loading form configuration..." });
    }
    if (!configRecord) {
        return React.createElement("div", { className: "bg-red-50 border border-red-200 rounded-md p-4 mx-6 mt-4" },
            React.createElement("div", { className: "flex" },
                React.createElement("div", { className: "flex-shrink-0" },
                    React.createElement(Icon, { name: "exclamation triangle", className: "h-5 w-5 text-red-400" })),
                React.createElement("div", { className: "ml-3" },
                    React.createElement("h3", { className: "text-sm font-medium text-red-800" }, "Error"),
                    React.createElement("div", { className: "mt-2 text-sm text-red-700" },
                        React.createElement("p", null, "Error loading form configuration. Please try again.")))));
    }
    const togglePreviewMaximized = () => {
        setIsPreviewMaximized(!isPreviewMaximized);
        // Force re-render of panel group to reset layout
        setPanelGroupKey(prev => prev + 1);
    };
    return (React.createElement(WorkspaceContainer, { "data-tour-id": "builder-root" },
        React.createElement(BuilderTour, { trigger: "auto" }),
        showTour && (React.createElement(BuilderTour, { trigger: "manual", onClose: () => setShowTour(false) })),
        React.createElement("button", { type: "button", onClick: () => setShowTour(true), "aria-label": "Builder help and walkthrough", style: {
                position: 'fixed',
                right: '24px',
                bottom: '24px',
                zIndex: 9000,
                borderRadius: '9999px',
                border: 'none',
                boxShadow: '0 10px 25px rgba(15,23,42,0.25)',
                background: '#f97316',
                color: 'white',
                width: '44px',
                height: '44px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                fontSize: '22px'
            } }, "?"),
        saveStatus === 'error' && !saveBannerDismissed && (React.createElement(SaveFailedBanner, { onRetry: () => {
                setSaveStatus('saving');
                debouncedAutosave();
            }, onDismiss: () => setSaveBannerDismissed(true) })),
        React.createElement(PanelGroup, { key: panelGroupKey, autoSaveId: isPreviewMaximized ? undefined : "builder-workspace", direction: "horizontal", style: { height: '100%', width: '100%' } },
            React.createElement(Panel, { defaultSize: 50, minSize: 25, maxSize: isPreviewMaximized ? 100 : 75, style: { position: 'relative' } },
                React.createElement(FluidFormPreview, { formKey: formKey, isPreviewMaximized: isPreviewMaximized, togglePreviewMaximized: togglePreviewMaximized, publishStatus: publishStatus, onPublishClick: () => switchTabRef.current?.('publish') })),
            React.createElement(ResizeHandle, null),
            React.createElement(Panel, { defaultSize: 50, minSize: isPreviewMaximized ? 0 : 25, maxSize: isPreviewMaximized ? 5 : 75, collapsible: isPreviewMaximized }, !isPreviewMaximized && (React.createElement(BuilderPanelGroup, { previewFormKey: formKey, configRecord: configRecord, onConfigRecordChange: handleConfigRecordChange, saveStatus: saveStatus, canUndo: history.canUndo, canRedo: history.canRedo, onTabSwitcherReady: (fn) => { switchTabRef.current = fn; }, onUndo: () => {
                    const snapshot = history.undo();
                    if (snapshot)
                        applySnapshot(snapshot);
                }, onRedo: () => {
                    const snapshot = history.redo();
                    if (snapshot)
                        applySnapshot(snapshot);
                }, headerExtraActions: React.createElement(TemplateToggleButton, { formKey: formKey, isTemplate: isTemplate, onToggled: (next) => setConfigRecord((prev) => (prev ? { ...prev, isTemplate: next } : prev)) }) }))))));
}
